<template>
  <div class="offer-information">
    <action-dispatcher
      action="product/getProduct"
      :parameters="$route.params.id"
    >
      <div class="d-flex flex-column gap-4">
        <div class="d-flex justify-content-between align-items-center mb-5">
          <h5 class="text-semibold">
            {{ editing ? "Editar oferta" : "Nova oferta" }}
          </h5>

          <div class="d-flex gap-3">
            <snow-button
              size="sm"
              text="Cancelar"
              variant="gray"
              @click="
                $router.push({
                  name: 'product-prices',
                  params: { id: $route.params.id }
                })
              "
            />
            <snow-button
              size="sm"
              text="Salvar Oferta"
              :loading="loadingSubmit"
              :disabled="!isValid"
              @click="submit"
            />
          </div>
        </div>

        <snow-input
          v-model="form.name"
          id="name"
          label="Nome da oferta"
          info="Este nome será exibido apenas para você, em sua tabela de preços."
          :limit="250"
          required
          :errors="errors.name"
        />

        <snow-input
          v-model="form.description"
          class="mb-4"
          id="description"
          textarea
          label="Descrição no checkout"
          info="Atenção! Esta é a descrição que exibiremos para os seus compradores."
          rows="5"
          :limit="980"
          required
          :errors="errors.description"
        />

        <h5 class="text-semibold">Origem de vendas</h5>
        <snow-input
          v-model="form.sck"
          class="mb-4"
          id="sck"
          label="SCK"
          placeholder="Digite o parâmetro desejado"
          info="Esse parâmetro te ajudará a identificar a origem da compra."
        />

        <h5 class="text-semibold">Definir período da oferta</h5>

        <div class="row">
          <div class="col-lg-6">
            <snow-date-picker
              isHours
              v-model="form.start_at"
              size="lg"
              format="YYYY-MM-DD HH:mm"
            />
            <span class="text-secondary fs-14">
              Escolha a data a partir da qual a oferta ficará disponível
            </span>
          </div>

          <div class="col-lg-6">
            <snow-date-picker
              isHours
              v-model="form.end_at"
              size="lg"
              format="YYYY-MM-DD HH:mm"
            />
            <span class="text-secondary fs-14">
              Escolha a data a partir da qual a oferta ficará indisponível
            </span>
          </div>
        </div>

        <snow-input
          v-model="form.expire_url"
          id="expire_url"
          label="URL para redirecionar após oferta expirar"
          :errors="errors.expire_url"
        />

        <snow-switch v-model="form.is_stock" label="Ativar/Desativar estoque" />

        <div v-if="form.is_stock">
          <div class="row">
            <div class="col-md-6">
              <snow-input
                v-model="form.stock"
                id="stock"
                label="Quantidade em estoque"
                :info="`Vendas realizadas do estoque: ${form.stock_sales}`"
                :min="1"
                mask="##########"
                required
                :errors="errors.stock"
              />
            </div>

            <div class="col-md-6">
              <snow-input
                v-model="form.stock_empty_url"
                id="stock_empty_url"
                label="URL para redirecionar após estoque acabar"
                :errors="errors.stock_empty_url"
              />
            </div>
          </div>
        </div>

        <h5 class="text-semibold mt-4">Precificação</h5>

        <snow-input
          v-model="form.price"
          money
          required
          label="Valor em Real Brasileiro (R$)"
          info="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que R$ 4,00."
          :min="4"
          :errors="errors.price"
          :moneyConfig="moneyConfig"
        />
        <snow-input
          v-if="!isParcelledBillet"
          :disabled="!showForeign"
          :info="getInfo('USD')"
          v-model="form.price_usd"
          money
          label="Valor em Dólar Americano (US$)"
          :min="4"
          :errors="errors.price_usd"
          :moneyConfig="{ ...moneyConfig, currency: '$' }"
        />
        <snow-input
          v-if="!isParcelledBillet"
          :disabled="!showForeign"
          :info="getInfo('EUR')"
          v-model="form.price_eur"
          money
          label="Valor em Euro (€)"
          :min="4"
          :errors="errors.price_eur"
          :moneyConfig="{ ...moneyConfig, currency: '€' }"
        />

        <h5 class="text-semibold mt-4">Configurar Order Bump</h5>

        <action-dispatcher action="product/get" :parameters="{ limit: 1000 }">
          <div class="d-flex flex-column gap-4">
            <order-bump-add
              v-for="(order_bump, i) in getListedOrderBumps()"
              :counter="i + 1"
              :form="order_bump"
              :key="order_bump.index"
              :product-id="$route.params.id"
              @remove="removeItem(order_bump, order_bump.index)"
            />
          </div>
        </action-dispatcher>
        <button class="add-order-bump" @click="form.order_bumps.push({})">
          <div class="d-flex align-items-center text-dark">
            <i class="ph ph-plus mr-4" />
            <span>Novo Order Bump</span>
          </div>
        </button>
      </div>
    </action-dispatcher>
  </div>
</template>

<script>
import OrderBumpAdd from "@/components/product/order-bump/OrderBumpAdd";
import {
  formatMoney,
  formFields,
  toComputerDate,
  toHumanDate
} from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

export default {
  components: { OrderBumpAdd },
  data() {
    return {
      loading: false,
      isActive: true,
      loadingSubmit: false,
      ...formFields(
        [
          "id",
          "name",
          "description",
          "price",
          "price_usd",
          "price_eur",
          "currency",
          "installments",
          "is_main",
          "status",
          "start_at",
          "end_at",
          "order_bump",
          "order_bumps",
          "sck",
          "expire_url",
          "is_stock",
          "stock",
          "stock_empty_url"
        ],
        {
          status: "active",
          order_bumps: [{}],
          installments: 1,
          is_stock: false,
          currency: "BRL",
          is_main: false
        }
      ),
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      }
    };
  },
  computed: {
    ...mapState("product", ["product", "products"]),
    ...mapState("offer", ["offer", "orderBumpOffers"]),
    ...mapState("sale", ["installments"]),
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length <= 980 &&
        (!this.form.is_stock || this.form.stock > 0) &&
        this.form.start_at &&
        this.form.currency &&
        this.form.price &&
        this.form.price >= 4 &&
        (!this.hasUSD || this.form.price_usd >= 4) &&
        (!this.hasEUR || this.form.price_eur >= 4) &&
        !this.form.order_bumps.some(
          order_bump => !!order_bump.product_id && !order_bump.offer_id
        )
      );
    },
    installmentOptions() {
      const opts = [];
      for (let id in this.installments) {
        opts.push({
          id: parseInt(id),
          name: `Até ${id} parcela${id - 1 ? "s" : ""} de R$ ${formatMoney(
            this.installments[id]
          )}`
        });
      }
      return opts;
    },
    editing() {
      return !!this.$route.params.offer_id;
    },
    showForeign() {
      return this.product.payment_methods?.some(({ id }) => id === 1);
    },
    hasUSD() {
      return !!this.form.price_usd && this.form.price_usd > 0;
    },
    hasEUR() {
      return !!this.form.price_eur && this.form.price_eur > 0;
    },
    isParcelledBillet() {
      return this.product?.billet_installments > 1;
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito. Atualize a configuração de "Opções de Pagamento" do seu produto em "Configurar Checkout"`;
    },
    getListedOrderBumps() {
      const bumps = [];
      for (let i in this.form.order_bumps) {
        this.form.order_bumps[i].index = i;
        if (!this.form.order_bumps[i].deleted)
          bumps.push(this.form.order_bumps[i]);
      }
      return bumps;
    },
    submit() {
      if (this.loadingSubmit) return;

      if (!this.form.start_at) {
        this.$message.warning(
          "É necessário escolher uma data válida de início antes de salvar."
        );
        return;
      }

      this.loadingSubmit = true;

      const data = {
        ...this.form,
        product_id: this.$route.params.id,
        end_at: toComputerDate(this.form.end_at),
        start_at: toComputerDate(this.form.start_at),
        status: this.isActive ? "active" : "inactive"
      };

      if (data.order_bumps) {
        data.order_bumps = data.order_bumps.filter(offer => offer.offer_id);
      }

      data.price_eur = parseFloat(data.price_eur) === 0 ? "" : data.price_eur;
      data.price_usd = parseFloat(data.price_usd) === 0 ? "" : data.price_usd;

      const action = this.$route.params.offer_id ? "offer/put" : "offer/post";

      this.$store
        .dispatch(action, data)
        .then(() => {
          this.$message.success("Oferta salva com sucesso");
          this.$router.push({
            name: "product-prices",
            params: { id: this.$route.params.id }
          });
        })
        .catch(error => {
          this.errors = error.response.data;
          this.$message.error("Erro ao salvar oferta");
        })
        .finally(() => (this.loadingSubmit = false));
    },
    removeItem(item, index) {
      if (item.code) {
        this.form.order_bumps[index] = { ...item, deleted: true };
        this.$forceUpdate();
      } else {
        this.form.order_bumps.splice(index, 1);
      }
    }
  },
  mounted() {
    if (this.$route.params.offer_id) {
      this.loading = true;
      this.$store
        .dispatch("offer/getOffer", this.$route.params.offer_id)
        .then(data => {
          this.form = {
            ...this.form,
            ...data,
            start_at: toHumanDate(data.start_at),
            end_at: toHumanDate(data.end_at),
            order_bumps: data.order_bumps?.map(item => ({
              product_id: item.offer?.product,
              offer_id: item.offer?.id,
              code: item.code
            })),
            price_usd: data.price_usd || "",
            price_eur: data.price_eur || ""
          };
          this.isActive = data.status === "active";
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>
<style scoped lang="scss">
.offer-information {
  background: var(--bg-2);
  padding: 24px;
  border-radius: 16px;

  .add-order-bump {
    display: flex;
    align-self: stretch;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    height: 68px;
    padding: 24px;
    width: 100%;
    border-radius: 16px;
    background: transparent;
    border: 1px dashed var(--black-20, rgba(0, 0, 0, 0.2));
  }
}
</style>
